import { API_BASE } from './trpc';

/**
 * Thin wrapper over better-auth's HTTP surface.
 *
 * The library is not imported into the browser bundle on purpose: the client only needs
 * three endpoints, and the session itself lives in an httpOnly cookie the SPA can never
 * read anyway. Session *state* comes from `session.me`, which reads the live user row.
 */

export interface SignInResult {
  ok: boolean;
  twoFactorRequired: boolean;
  error?: string;
}

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(`${API_BASE}/api/auth${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const res = await post('/sign-in/email', { email, password });
  if (res.ok) {
    const data = (await res.json()) as { twoFactorRedirect?: boolean };
    return { ok: true, twoFactorRequired: data.twoFactorRedirect === true };
  }
  if (res.status === 429) {
    return { ok: false, twoFactorRequired: false, error: 'Забагато спроб. Спробуйте за хвилину.' };
  }
  const detail = (await res.json().catch(() => null)) as { message?: string } | null;
  return {
    ok: false,
    twoFactorRequired: false,
    error: detail?.message ?? 'Невірний email або пароль.',
  };
}

export async function verifyTotp(code: string): Promise<SignInResult> {
  const res = await post('/two-factor/verify-totp', { code });
  if (res.ok) return { ok: true, twoFactorRequired: false };
  return { ok: false, twoFactorRequired: true, error: 'Невірний код.' };
}

export async function signOut(): Promise<void> {
  await post('/sign-out', {});
}

/**
 * Anti-enumeration by design: the endpoint always answers `{ status: true }`, whether or not
 * the email belongs to a real account. A rejected promise here means the request itself never
 * reached the server (network failure), not "email unknown" — the caller shows the same
 * generic message either way and reserves an error state for the network case.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await post('/request-password-reset', { email });
}

export interface ResetPasswordResult {
  ok: boolean;
}

export async function resetPassword(
  newPassword: string,
  token: string,
): Promise<ResetPasswordResult> {
  const res = await post('/reset-password', { newPassword, token });
  return { ok: res.ok };
}

export interface EnableTwoFactorResult {
  ok: boolean;
  totpURI?: string;
  backupCodes?: string[];
  /**
   * The API's own code on a refusal, when it sent one. `SSO_STEP_UP_REQUIRED` is the one the
   * setup page acts on: the SSO-only account's fresh-sign-in grant is used up or expired.
   */
  code?: string;
}

/**
 * Asks for the TOTP secret. With a password for an account that has one; with nothing at all
 * for an SSO-only account — there the proof is the corporate SSO sign-in the session just
 * came from, which the API checks server-side (`apps/api/src/auth/sso-enrolment.ts`).
 */
export async function enableTwoFactor(password?: string): Promise<EnableTwoFactorResult> {
  const res = await post('/two-factor/enable', password === undefined ? {} : { password });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { code?: string } | null;
    return typeof detail?.code === 'string' ? { ok: false, code: detail.code } : { ok: false };
  }
  const data = (await res.json()) as { totpURI: string; backupCodes: string[] };
  return { ok: true, totpURI: data.totpURI, backupCodes: data.backupCodes };
}
